/**
 * White-label display helpers — visual only; internal keys, API payloads, and routing unchanged.
 */
import { BRAND_NAME } from "./branding.ts";

const AGENT_ID_LABELS: Record<string, string> = {
  main: "Assistente principal",
  principal: "Assistente principal",
};

const SESSION_KIND_LABELS: Record<string, string> = {
  direct: "Direto",
  group: "Grupo",
  cron: "Agendado",
  global: "Global",
  explicit: "Conversa privada",
  unknown: "Outro",
};

const CHANNEL_KIND_LABELS: Record<string, string> = {
  explicit: "Conversa privada",
  direct: "Direto",
};

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function capitalize(s: string): string {
  if (!s) {
    return s;
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function isInternalLinuxPath(path: string): boolean {
  return (
    path.startsWith("/home/") ||
    path.startsWith("/var/") ||
    path.startsWith("/root/") ||
    /\.openclaw/i.test(path)
  );
}

export function formatAgentIdForDisplay(agentId: string): string {
  const raw = agentId.trim();
  if (!raw) {
    return "—";
  }
  const mapped = AGENT_ID_LABELS[raw.toLowerCase()];
  if (mapped) {
    return mapped;
  }
  return capitalize(raw.replace(/[_-]+/g, " "));
}

export function formatSessionKindForDisplay(kind: string | null | undefined): string {
  const k = (kind ?? "").trim().toLowerCase();
  if (!k) {
    return "—";
  }
  return SESSION_KIND_LABELS[k] ?? capitalize(k);
}

export function formatChannelTokenForDisplay(channel: string): string {
  const c = channel.trim().toLowerCase();
  return CHANNEL_KIND_LABELS[c] ?? capitalize(c);
}

/** Workspace / filesystem paths — never show vendor container paths in product UI. */
export function formatWorkspacePathForDisplay(path: string | null | undefined): string {
  const trimmed = (path ?? "").trim();
  if (!trimmed || trimmed === "default") {
    return `Área de trabalho ${BRAND_NAME}`;
  }
  if (isInternalLinuxPath(trimmed)) {
    const segments = trimmed.split(/[/\\]+/).filter(Boolean);
    const leaf = segments[segments.length - 1];
    if (!leaf || leaf === "workspace" || leaf === ".openclaw") {
      return `Área de trabalho ${BRAND_NAME}`;
    }
    return `Pasta do projeto · ${leaf}`;
  }
  return trimmed;
}

export function isOpaqueTechnicalId(value: string): boolean {
  const v = value.trim();
  if (!v) {
    return false;
  }
  if (UUID_RE.test(v)) {
    return true;
  }
  if (/^[0-9a-f]{20,}$/i.test(v)) {
    return true;
  }
  if (v.startsWith("agent:") && v.length > 24) {
    return true;
  }
  return false;
}

/** Session / correlation ids — hide from end-user panels when opaque. */
export function formatPublicSessionIdForDisplay(
  sessionId: string | null | undefined,
): string | null {
  const s = (sessionId ?? "").trim();
  if (!s || isOpaqueTechnicalId(s)) {
    return null;
  }
  return scrubTechnicalDisplayText(s);
}

export function scrubTechnicalDisplayText(text: string): string {
  let out = text;
  out = out.replace(/\bagent:principal(?::[^\s?]*)?/gi, "Sessão principal");
  out = out.replace(/\bagent:main:main\b/gi, "Sessão principal");
  const tokenIdx = out.indexOf("?token=");
  if (tokenIdx >= 0) {
    out = out.slice(0, tokenIdx);
  }
  out = out.replace(
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
    "···",
  );
  out = out.replace(/\b[0-9a-f]{24,}\b/gi, "···");
  out = out.replace(/\/home\/[^\s]+/gi, `Área de trabalho ${BRAND_NAME}`);
  out = out.replace(/\.openclaw[^\s]*/gi, `Área de trabalho ${BRAND_NAME}`);
  return out.trim();
}

export function formatRuntimeLabelForDisplay(runtime: string): string {
  const r = runtime.trim();
  if (!r || /^pi\b/i.test(r) || r.includes("openclaw")) {
    return `${BRAND_NAME} runtime`;
  }
  return scrubTechnicalDisplayText(r);
}
